import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import {
  TableBody,
  TableCell,
  TableRow,
  // Switch,
} from '@mui/material';
import basicServiceHandler from '../../../../services/basicServiceHandler';
import { convertAxiosErrorToUserFacingMessage } from '../../../../context/ApiClientContext';
import useTable from '../../../../components/useTable';
import AccuroSettingCell from './AccuroSettingCell';

const EDITABLE_FIELDS = [
  'daysBeforeAppointment',
  'appointmentType',
  'filterByProviderIds',
  'providerIds',
  'sendDays',
  'appointmentReasonIds',
  'folderId',
  'subFolderId',
  'sentSMSStatus',
  'notSentSMSStatus',
  'intakeCompletedStatus',
];

const headers = [
  { id: 'automationSetting', label: 'Automation Setting' },
  { id: 'value', label: 'Value' },
  { id: 'action', label: 'Action' },
];

export default function AccuroSettingTable(props) {
  const {
    entity,
    accuroSettings,
    handleSave,
    refreshParent,
    setSuccessMessage,
    setErrorMessage,
    setValues,
    apiClient,
  } = props;
  const { siteId } = useParams();

  const [accuroProviders, setAccuroProviders] = useState([]);
  const [accuroAppointmentReasons, setAccuroAppointmentReasons] = useState([]);
  const [accuroAppointmentTypes, setAccuroAppointmentTypes] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState([]);

  const editableSettings = Object.entries(accuroSettings).filter(
    ([key, setting]) => EDITABLE_FIELDS.includes(key) && setting && setting.value !== undefined,
  );

  const {
    TblContainer,
    TblHead,
  } = useTable(editableSettings, headers, { fn: (items) => items });

  const getAccuroProviders = async () => {
    basicServiceHandler(
      () => apiClient.getAccuroProviders(siteId),
      (response) => {
        setAccuroProviders(response.data);
      },
      (error) => {
        setErrorMessage(convertAxiosErrorToUserFacingMessage(error));
      },
    );
  };

  const getAccuroAppointmentReasons = async () => {
    basicServiceHandler(
      () => apiClient.getAccuroAppointmentReasons(siteId),
      (response) => {
        setAccuroAppointmentReasons(response.data);
      },
      (error) => {
        setErrorMessage(convertAxiosErrorToUserFacingMessage(error));
      },
    );
  };

  const getAccuroAppointmentTypes = async () => {
    basicServiceHandler(
      () => apiClient.getAccuroAppointmentTypes(siteId),
      (response) => {
        setAccuroAppointmentTypes(response.data);
      },
      (error) => {
        setErrorMessage(convertAxiosErrorToUserFacingMessage(error));
      },
    );
  };

  const getSelectedPlans = (intakePlanList) => {
    const availableIntakePlans = accuroSettings.intakePlans.value;
    if (availableIntakePlans === '*') {
      return intakePlanList;
    }
    if (availableIntakePlans === '') {
      return [];
    }
    return intakePlanList.filter((intakePlan) => availableIntakePlans.split(',').includes(intakePlan.id.toString()));
  };

  async function getIntakePlans() {
    basicServiceHandler(
      () => apiClient.getIntakePlans(),
      (response) => {
        const plans = getSelectedPlans(response.data);
        setSelectedPlans(plans);
      },
      (error) => {
        setErrorMessage(convertAxiosErrorToUserFacingMessage(error));
        return [];
      },
    );
  }

  useEffect(() => {
    if (
      accuroSettings.accessToken.value
      && accuroSettings.accessTokenExpireTime.value
      && new Date(accuroSettings.accessTokenExpireTime.value) > new Date()
    ) {
      getAccuroProviders();
      getAccuroAppointmentReasons();
      getAccuroAppointmentTypes();
    }
  }, []);

  useEffect(() => {
    if (accuroSettings.intakePlans && accuroSettings.intakePlans.value) {
      getIntakePlans();
    }
  }, [accuroSettings]);

  return (
    <TblContainer>
      <TblHead />
      <TableBody>
        {editableSettings.map(([key, setting]) => {
          const { isDefault, ...settingValues } = setting;
          return (
            <TableRow key={key}>
              <TableCell>{key}</TableCell>
              <AccuroSettingCell
                entity={entity}
                setting={settingValues}
                handleSave={handleSave}
                setValues={setValues}
                refreshParent={refreshParent}
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
                apiClient={apiClient}
                accuroAppointmentReasons={accuroAppointmentReasons}
                accuroProviders={accuroProviders}
                accuroAppointmentTypes={accuroAppointmentTypes}
                selectedPlans={selectedPlans}
              />
            </TableRow>
          );
        })}
      </TableBody>
    </TblContainer>
  );
}

// First, define a reusable setting shape
const settingShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

AccuroSettingTable.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    settings: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.string,
      value: PropTypes.string,
    })),
  }).isRequired,
  handleSave: PropTypes.func.isRequired,
  accuroSettings: PropTypes.shape({
    enabled: settingShape.isRequired,
    uuid: settingShape.isRequired,
    accessToken: settingShape.isRequired,
    refreshToken: settingShape.isRequired,
    accessTokenExpireTime: settingShape.isRequired,
    refreshTokenExpireTime: settingShape.isRequired,
    sentSMSStatus: settingShape.isRequired,
    notSentSMSStatus: settingShape.isRequired,
    intakeCompletedStatus: settingShape.isRequired,
    preBookedAutomationEnabled: settingShape.isRequired,
    sameDayAutomationEnabled: settingShape.isRequired,
    daysBeforeAppointment: settingShape.isRequired,
    folderId: settingShape.isRequired,
    subFolderId: settingShape.isRequired,
    appointmentType: settingShape.isRequired,
    sendDays: settingShape.isRequired,
    appointmentReasonIds: settingShape.isRequired,
    filterByProviderIds: settingShape.isRequired,
    providerIds: settingShape.isRequired,
    intakePlans: settingShape.isRequired,
  }).isRequired,
  apiClient: PropTypes.shape({
    getAccuroProviders: PropTypes.func.isRequired,
    getAccuroAppointmentReasons: PropTypes.func.isRequired,
    getAccuroAppointmentTypes: PropTypes.func.isRequired,
    getIntakePlans: PropTypes.func.isRequired,
  }).isRequired,
  setValues: PropTypes.func.isRequired,
  refreshParent: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};
